import { createContext, useContext, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import React from 'react';

// Create a context to hold reset functions
type ResetFunction = () => void;
const ResetContext = createContext<{
  registerReset: (resetFn: ResetFunction) => void;
  resetAll: () => void;
}>({
  registerReset: () => {},
  resetAll: () => {},
});

// Custom hook to access reset functions
export const useResetState = () => useContext(ResetContext);

// Provider component to manage reset functions
export function ResetProvider({ children }: { children: React.ReactNode }) {
  const resetFunctions = useRef<ResetFunction[]>([]);
  const navigate = useNavigate();

  const registerReset = useCallback((resetFn: ResetFunction) => {
    resetFunctions.current.push(resetFn);
    
    // Return cleanup function
    return () => {
      resetFunctions.current = resetFunctions.current.filter(fn => fn !== resetFn);
    };
  }, []);

  const resetAll = useCallback(() => {
    // Reset all registered states
    resetFunctions.current.forEach(fn => {
      try {
        fn();
      } catch (error) {
        console.error('Error during state reset:', error);
      }
    });
    
    // Clear any remaining state in localStorage/sessionStorage
    localStorage.clear();
    sessionStorage.clear();
    
    // Force a hard navigation to clear React Router state
    navigate('/login', { replace: true });
  }, [navigate]);

  const value = useMemo(() => ({
    registerReset,
    resetAll,
  }), [registerReset, resetAll]);

  return React.createElement(
    ResetContext.Provider,
    { value },
    children
  );
}
